import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import MobileScoreEntry from './MobileScoreEntry';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Edit,
  CheckCircle,
  Cancel,
  Person,
  Group as GroupIcon,
  ClearAll
} from '@mui/icons-material';

export default function ScorerInterface() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { userProfile, currentUser } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teeTimes, setTeeTimes] = useState([]);
  const [selectedTeeTime, setSelectedTeeTime] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(2); // Default to Table entry view
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingHole, setEditingHole] = useState(null);
  const [editingScore, setEditingScore] = useState('');
  const [pendingScores, setPendingScores] = useState({});
  const [pendingTableScores, setPendingTableScores] = useState({});
  const [activeHole, setActiveHole] = useState(null);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

      setTeeTimes(tournamentData.flights || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlayer = (playerId) => {
    const player = players.find(p => p.id === playerId);
    setSelectedPlayer(player);

    // Load existing scores for this player
    const existingScores = tournament.scores?.[playerId] || {};
    setScores(existingScores);
  };

  const handleScoreChange = async (hole, value) => {
    // Remove non-numeric characters
    const cleanValue = value.replace(/[^0-9]/g, '');
    
    if (cleanValue === '') {
      setScores(prev => ({
        ...prev,
        [hole]: ''
      }));
      return;
    }

    const numValue = parseInt(cleanValue);
    
    // Validate range
    if (numValue > 20) return;

    // Update the score display
    setScores(prev => ({
      ...prev,
      [hole]: numValue
    }));

    // Handle auto-save logic
    if (cleanValue.length === 1) {
      // Single digit
      if (numValue === 1) {
        // Wait for potential second digit (for 10-19)
        setPendingScores(prev => ({ ...prev, [hole]: numValue }));
        return;
      } else {
        // Auto-save single digits 2-9
        await saveHoleScore(hole, numValue);
      }
    } else if (cleanValue.length === 2) {
      // Two digits (10-19)
      if (numValue >= 10 && numValue <= 20) {
        await saveHoleScore(hole, numValue);
      }
    }
  };

  const saveHoleScore = async (hole, score) => {
    if (!selectedPlayer) return;

    try {
      const updatedScores = {
        ...scores,
        [hole]: score
      };

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        [`scores.${selectedPlayer.id}`]: updatedScores,
        [`lastUpdated.${selectedPlayer.id}`]: new Date(),
        [`scoredBy.${selectedPlayer.id}`]: {
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.email || userProfile?.email || 'Unknown Scorer',
          timestamp: new Date()
        }
      });

      // Update local state
      setScores(updatedScores);
      setPendingScores(prev => {
        const newPending = { ...prev };
        delete newPending[hole];
        return newPending;
      });

    } catch (error) {
      console.error('Error saving hole score:', error);
      alert(`Error saving score for hole ${hole}: ${error.message}`);
    }
  };

  const handleKeyPress = async (e, hole) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const score = scores[hole];
      if (score) {
        await saveHoleScore(hole, parseInt(score));
        // Move to next hole
        const nextHole = hole < 18 ? hole + 1 : hole;
        setActiveHole(nextHole);
        // Focus next input
        const nextInput = document.querySelector(`input[data-hole="${nextHole}"]`);
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    }
  };

  const handleTableScoreChange = async (playerId, hole, value) => {
    // Remove non-numeric characters
    const cleanValue = value.replace(/[^0-9]/g, '');
    
    if (cleanValue === '') {
      // Clear the score and pending
      setPendingTableScores(prev => {
        const newPending = { ...prev };
        delete newPending[`${playerId}_${hole}`];
        return newPending;
      });
      await saveTableHoleScore(playerId, hole, '');
      return;
    }

    const numValue = parseInt(cleanValue);
    
    // Validate range
    if (numValue > 20) return;

    // Handle auto-save logic
    if (cleanValue.length === 1) {
      // Single digit
      if (numValue === 1) {
        // Wait for potential second digit (for 10-19)
        setPendingTableScores(prev => ({
          ...prev,
          [`${playerId}_${hole}`]: numValue
        }));
        return;
      } else {
        // Auto-save single digits 2-9 and move to next hole
        setPendingTableScores(prev => {
          const newPending = { ...prev };
          delete newPending[`${playerId}_${hole}`];
          return newPending;
        });
        await saveTableHoleScore(playerId, hole, numValue);
        
        // Move to next hole
        const nextHole = hole < 18 ? hole + 1 : hole;
        setActiveHole(nextHole);
        
        // Focus next input after a short delay to allow the save to complete
        setTimeout(() => {
          const nextInput = document.querySelector(`input[data-hole="${nextHole}"][data-player="${playerId}"]`);
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
          }
        }, 100);
      }
    } else if (cleanValue.length === 2) {
      // Two digits (10-19)
      if (numValue >= 10 && numValue <= 20) {
        setPendingTableScores(prev => {
          const newPending = { ...prev };
          delete newPending[`${playerId}_${hole}`];
          return newPending;
        });
        await saveTableHoleScore(playerId, hole, numValue);
        
        // Move to next hole
        const nextHole = hole < 18 ? hole + 1 : hole;
        setActiveHole(nextHole);
        
        // Focus next input after a short delay to allow the save to complete
        setTimeout(() => {
          const nextInput = document.querySelector(`input[data-hole="${nextHole}"][data-player="${playerId}"]`);
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
          }
        }, 100);
      }
    }
  };

  const saveTableHoleScore = async (playerId, hole, score) => {
    try {
      const currentScores = tournament.scores?.[playerId] || {};
      const updatedScores = {
        ...currentScores,
        [hole]: score
      };

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        [`scores.${playerId}`]: updatedScores,
        [`lastUpdated.${playerId}`]: new Date(),
        [`scoredBy.${playerId}`]: {
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.email || userProfile?.email || 'Unknown Scorer',
          timestamp: new Date()
        }
      });

      // Update local tournament state
      setTournament(prev => ({
        ...prev,
        scores: {
          ...prev.scores,
          [playerId]: updatedScores
        }
      }));

    } catch (error) {
      console.error('Error saving table hole score:', error);
      alert(`Error saving score for ${playerId} hole ${hole}: ${error.message}`);
    }
  };

  const handleTableKeyPress = async (e, playerId, hole) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.target;
      const score = input.value;
      if (score) {
        // Clear any pending score and save
        setPendingTableScores(prev => {
          const newPending = { ...prev };
          delete newPending[`${playerId}_${hole}`];
          return newPending;
        });
        await saveTableHoleScore(playerId, hole, parseInt(score));
        // Move to next hole for same player
        const nextHole = hole < 18 ? hole + 1 : hole;
        setActiveHole(nextHole);
        // Focus next input
        const nextInput = document.querySelector(`input[data-hole="${nextHole}"][data-player="${playerId}"]`);
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handleSaveScores = async () => {
    if (!selectedPlayer) {
      alert('Please select a player first');
      return;
    }

    try {
      setSaving(true);

      // Save any pending scores
      for (const [hole, score] of Object.entries(scores)) {
        if (score) {
          await saveHoleScore(parseInt(hole), parseInt(score));
        }
      }
      
      // Refresh tournament data
      fetchData();

    } catch (error) {
      console.error('Error saving scores:', error);
      alert('Error saving scores: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClearScores = () => {
    if (window.confirm('Are you sure you want to clear all scores for this player?')) {
      setScores({});
    }
  };

  const handleClearAllScores = async () => {
    try {
      setSaving(true);
      
      // Clear all scores for all players
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const updates = {
        scores: {},
        lastUpdated: {},
        scoredBy: {}
      };
      
      await updateDoc(tournamentRef, updates);
      
      // Clear local state
      setScores({});
      setSelectedPlayer(null);
      setPendingScores({});
      setPendingTableScores({});
      
      // Refresh tournament data
      await fetchData();
      
      setClearAllDialogOpen(false);
      alert('All scores have been cleared successfully!');
      
    } catch (error) {
      console.error('Error clearing all scores:', error);
      alert('Error clearing all scores: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickEdit = (hole) => {
    setEditingHole(hole);
    setEditingScore(scores[hole] || '');
    setOpenEditDialog(true);
  };

  const handleSaveQuickEdit = () => {
    handleScoreChange(editingHole, editingScore);
    setOpenEditDialog(false);
  };

  const calculateTotal = () => {
    return Object.values(scores).reduce((total, score) => {
      return total + (parseInt(score) || 0);
    }, 0);
  };

  const calculateNetScore = () => {
    if (!selectedPlayer?.handicap || tournament?.type === 'STROKEPLAY_SCRATCH') {
      return calculateTotal();
    }
    return calculateTotal() - Math.floor(selectedPlayer.handicap);
  };

  const getCompletedHoles = () => {
    return Object.keys(scores).filter(hole => scores[hole] && scores[hole] !== '').length;
  };

  const getPlayersByTeeTime = (teeTimeId) => {
    const teeTime = teeTimes.find(t => t.id === teeTimeId);
    if (!teeTime) return [];
    
    return teeTime.playerIds?.map(playerId => 
      players.find(p => p.id === playerId)
    ).filter(p => p) || [];
  };

  const getPlayerCompletionStatus = (playerId) => {
    const playerScores = tournament.scores?.[playerId];
    if (!playerScores) return { status: 'not_started', holes: 0 };
    
    const holes = Object.keys(playerScores).length;
    if (holes === 0) return { status: 'not_started', holes: 0 };
    if (holes === 18) return { status: 'complete', holes: 18 };
    return { status: 'in_progress', holes };
  };

  const getHoleScoreStyle = (score, holePar) => {
    if (!score || !holePar) return { bgcolor: 'white', color: 'black' };
    
    const diff = score - holePar;
    
    // Hole in one (ace)
    if (score === 1) {
      return { 
        bgcolor: 'black', 
        color: 'yellow',
        fontWeight: 'bold',
        border: '2px solid gold'
      };
    }
    
    // Eagle (2 under par)
    if (diff === -2) {
      return { 
        bgcolor: 'purple', 
        color: 'white',
        fontWeight: 'bold'
      };
    }
    
    // Birdie (1 under par)
    if (diff === -1) {
      return { 
        bgcolor: 'red', 
        color: 'white',
        fontWeight: 'bold'
      };
    }
    
    // Par
    if (diff === 0) {
      return { 
        bgcolor: 'white', 
        color: 'black',
        border: '1px solid #ddd'
      };
    }
    
    // Bogey (1 over par)
    if (diff === 1) {
      return { 
        bgcolor: 'black', 
        color: 'white',
        fontWeight: 'bold'
      };
    }
    
    // Double bogey or worse (2+ over par)
    return { 
      bgcolor: 'black', 
      color: 'white',
      fontWeight: 'bold',
      border: '2px solid red'
    };
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  // Render mobile component for mobile devices
  if (isMobile) {
    return <MobileScoreEntry />;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(`/tournaments/${tournamentId}/director`)}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">Official Scorer Interface</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {tournament.name}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          {selectedPlayer && (
            <>
              <Chip 
                label="Auto-saving enabled" 
                size="small" 
                color="success" 
                variant="outlined"
              />
              <Button
                variant="outlined"
                onClick={handleClearScores}
                startIcon={<Cancel />}
              >
                Clear Player
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            color="error"
            onClick={() => setClearAllDialogOpen(true)}
            startIcon={<ClearAll />}
            disabled={saving}
          >
            Clear All Scores
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Player Selection Panel - Hidden when Table Entry is selected */}
        {tabValue !== 2 && (
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>Select Player</Typography>

              <Tabs 
                value={tabValue} 
                onChange={(e, val) => setTabValue(val)} 
                sx={{ mb: 2 }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Tee Time" icon={<GroupIcon />} iconPosition="start" sx={{ minWidth: 'auto', px: 1 }} />
                <Tab label="Players" icon={<Person />} iconPosition="start" sx={{ minWidth: 'auto', px: 1 }} />
                <Tab label="Table" icon={<Edit />} iconPosition="start" sx={{ minWidth: 'auto', px: 1 }} />
            </Tabs>

            {tabValue === 0 && (
              <Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Tee Time</InputLabel>
                  <Select
                    value={selectedTeeTime}
                    label="Select Tee Time"
                    onChange={(e) => setSelectedTeeTime(e.target.value)}
                  >
                    {teeTimes.map(teeTime => (
                      <MenuItem key={teeTime.id} value={teeTime.id}>
                        {teeTime.name} ({teeTime.playerIds?.length || 0} players)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedTeeTime && (
                  <Box>
                    {getPlayersByTeeTime(selectedTeeTime).map(player => {
                      const status = getPlayerCompletionStatus(player.id);
                      return (
                        <Card 
                          key={player.id}
                          sx={{ 
                            mb: 1, 
                            cursor: 'pointer',
                            border: selectedPlayer?.id === player.id ? 2 : 0,
                            borderColor: 'primary.main'
                          }}
                          onClick={() => handleSelectPlayer(player.id)}
                        >
                          <CardContent sx={{ py: 1.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body1">
                                  {player.firstName} {player.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  HCP: {player.handicap || 0}
                                </Typography>
                              </Box>
                              <Box>
                                {status.status === 'complete' && (
                                  <Chip label="Complete" color="success" size="small" icon={<CheckCircle />} />
                                )}
                                {status.status === 'in_progress' && (
                                  <Chip label={`${status.holes}/18`} color="warning" size="small" />
                                )}
                                {status.status === 'not_started' && (
                                  <Chip label="Not Started" color="default" size="small" />
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {tabValue === 1 && (
              <Box>
                {players.map(player => {
                  const status = getPlayerCompletionStatus(player.id);
                  return (
                    <Card 
                      key={player.id}
                      sx={{ 
                        mb: 1, 
                        cursor: 'pointer',
                        border: selectedPlayer?.id === player.id ? 2 : 0,
                        borderColor: 'primary.main'
                      }}
                      onClick={() => handleSelectPlayer(player.id)}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1">
                              {player.firstName} {player.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              HCP: {player.handicap || 0}
                            </Typography>
                          </Box>
                          <Box>
                            {status.status === 'complete' && (
                              <Chip label="Complete" color="success" size="small" icon={<CheckCircle />} />
                            )}
                            {status.status === 'in_progress' && (
                              <Chip label={`${status.holes}/18`} color="warning" size="small" />
                            )}
                            {status.status === 'not_started' && (
                              <Chip label="Not Started" color="default" size="small" />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}

            {tabValue === 2 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Edit scores for all players in the table view below
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Click on any score cell in the table to edit scores for all players at once
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>
        )}

        {/* Score Entry Panel */}
        <Grid item xs={12} md={tabValue === 2 ? 12 : 8}>
          {tabValue === 2 ? (
            <Paper sx={{ p: 3, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
              {/* Compact Tab Bar for Table Entry */}
              <Box sx={{ mb: 3 }}>
                <Tabs 
                  value={tabValue} 
                  onChange={(e, val) => setTabValue(val)} 
                  sx={{ mb: 2 }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Tee Time" icon={<GroupIcon />} iconPosition="start" sx={{ minWidth: 'auto', px: 1 }} />
                  <Tab label="Players" icon={<Person />} iconPosition="start" sx={{ minWidth: 'auto', px: 1 }} />
                  <Tab label="Table" icon={<Edit />} iconPosition="start" sx={{ minWidth: 'auto', px: 1 }} />
                </Tabs>
              </Box>
              
              <Typography variant="h5" gutterBottom>Table Entry - All Players ({players.length} players)</Typography>
              
              {players.length === 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No players found. Please add players to the tournament first.
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      {/* Hole Numbers Row */}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: '150px', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                          Player
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: '60px', textAlign: 'center' }}>HCP</TableCell>
                        {HOLES.map(hole => (
                          <TableCell key={hole} sx={{ 
                            fontWeight: 'bold', 
                            minWidth: '50px', 
                            textAlign: 'center',
                            bgcolor: 'primary.light',
                            color: 'white',
                            fontSize: '0.8rem',
                            px: 0.5
                          }}>
                            {hole}
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 'bold', minWidth: '60px', textAlign: 'center' }}>Total</TableCell>
                      </TableRow>
                      {/* Par Row */}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100', position: 'sticky', left: 0, zIndex: 1 }}>Par</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100' }} />
                        {HOLES.map(hole => {
                          const holePar = tournament.golfCourse?.holes?.[hole - 1]?.par || 4;
                          return (
                            <TableCell key={hole} sx={{ 
                              fontWeight: 'bold',
                              bgcolor: 'grey.100',
                              fontSize: '0.8rem',
                              minWidth: '50px',
                              textAlign: 'center',
                              px: 0.5
                            }}>
                              {holePar}
                            </TableCell>
                          );
                        })}
                        <TableCell sx={{ bgcolor: 'grey.100' }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {players.map(player => {
                      const playerScores = tournament.scores?.[player.id] || {};
                      const totalScore = HOLES.reduce((sum, hole) => sum + (parseInt(playerScores[hole]) || 0), 0);
                      
                      return (
                        <TableRow key={player.id} hover>
                          <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {player.firstName} {player.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPlayerCompletionStatus(player.id).status === 'complete' ? 'Complete' : 
                                 getPlayerCompletionStatus(player.id).status === 'in_progress' ? `${getPlayerCompletionStatus(player.id).holes}/18` : 'Not Started'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{player.handicap || 0}</Typography>
                          </TableCell>
                          {HOLES.map(hole => {
                            const pendingScore = pendingTableScores[`${player.id}_${hole}`];
                            const displayValue = pendingScore !== undefined ? pendingScore : (playerScores[hole] || '');
                            const holePar = tournament.golfCourse?.holes?.[hole - 1]?.par || 4;
                            const scoreStyle = displayValue ? getHoleScoreStyle(parseInt(displayValue), holePar) : {};
                            
                            return (
                              <TableCell key={hole} align="center" sx={{ p: 0.5 }}>
                                {displayValue ? (
                                  <Box
                                    sx={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.8rem',
                                      fontWeight: 'bold',
                                      color: scoreStyle.color,
                                      bgcolor: scoreStyle.bgcolor,
                                      border: scoreStyle.border,
                                      mx: 'auto',
                                      position: 'relative'
                                    }}
                                  >
                                    <TextField
                                      type="text"
                                      size="small"
                                      value={displayValue}
                                      onChange={(e) => handleTableScoreChange(player.id, hole, e.target.value)}
                                      onKeyPress={(e) => handleTableKeyPress(e, player.id, hole)}
                                      onFocus={(e) => {
                                        setActiveHole(hole);
                                        e.target.select();
                                      }}
                                      inputProps={{ 
                                        maxLength: 2,
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        'data-hole': hole,
                                        'data-player': player.id,
                                        style: { 
                                          textAlign: 'center', 
                                          padding: 0,
                                          width: '100%',
                                          height: '100%',
                                          fontSize: '0.8rem',
                                          fontWeight: 'bold',
                                          color: scoreStyle.color,
                                          backgroundColor: 'transparent',
                                          border: 'none',
                                          outline: 'none'
                                        }
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        '& .MuiOutlinedInput-root': {
                                          height: '100%',
                                          '& fieldset': {
                                            border: 'none',
                                          },
                                          '&:hover fieldset': {
                                            border: 'none',
                                          },
                                          '&.Mui-focused fieldset': {
                                            border: 'none',
                                          },
                                        },
                                        '& input': {
                                          textAlign: 'center',
                                          padding: 0,
                                          height: '100%',
                                          color: scoreStyle.color,
                                          backgroundColor: 'transparent'
                                        }
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.8rem',
                                      color: 'text.secondary',
                                      bgcolor: 'grey.100',
                                      border: '1px solid #ddd',
                                      mx: 'auto',
                                      position: 'relative'
                                    }}
                                  >
                                    <TextField
                                      type="text"
                                      size="small"
                                      value={displayValue}
                                      onChange={(e) => handleTableScoreChange(player.id, hole, e.target.value)}
                                      onKeyPress={(e) => handleTableKeyPress(e, player.id, hole)}
                                      onFocus={(e) => {
                                        setActiveHole(hole);
                                        e.target.select();
                                      }}
                                      inputProps={{ 
                                        maxLength: 2,
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        'data-hole': hole,
                                        'data-player': player.id,
                                        style: { 
                                          textAlign: 'center', 
                                          padding: 0,
                                          width: '100%',
                                          height: '100%',
                                          fontSize: '0.8rem',
                                          fontWeight: 'bold',
                                          color: 'text.secondary',
                                          backgroundColor: 'transparent',
                                          border: 'none',
                                          outline: 'none'
                                        }
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        '& .MuiOutlinedInput-root': {
                                          height: '100%',
                                          '& fieldset': {
                                            border: 'none',
                                          },
                                          '&:hover fieldset': {
                                            border: 'none',
                                          },
                                          '&.Mui-focused fieldset': {
                                            border: 'none',
                                          },
                                        },
                                        '& input': {
                                          textAlign: 'center',
                                          padding: 0,
                                          height: '100%',
                                          color: 'text.secondary',
                                          backgroundColor: 'transparent'
                                        }
                                      }}
                                    />
                                  </Box>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {totalScore || 0}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          ) : !selectedPlayer ? (
            <Paper sx={{ p: 4, textAlign: 'center', height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box>
                <Person sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>No Player Selected</Typography>
                <Typography color="text.secondary">
                  Select a player from the list to enter scores
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: 3 }}>
              {/* Player Info */}
              <Box mb={3}>
                <Typography variant="h5" gutterBottom>
                  {selectedPlayer.firstName} {selectedPlayer.lastName}
                </Typography>
                <Box display="flex" gap={2}>
                  <Chip label={`Handicap: ${selectedPlayer.handicap || 0}`} />
                  <Chip label={`Holes: ${getCompletedHoles()}/18`} color="primary" />
                  <Chip label={`Gross: ${calculateTotal()}`} color="info" />
                  {tournament.type !== 'STROKEPLAY_SCRATCH' && (
                    <Chip label={`Net: ${calculateNetScore()}`} color="success" />
                  )}
                </Box>
              </Box>

              {/* Score Entry Table */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center"><strong>Hole</strong></TableCell>
                      {HOLES.slice(0, 9).map(hole => (
                        <TableCell key={hole} align="center"><strong>{hole}</strong></TableCell>
                      ))}
                      <TableCell align="center"><strong>Out</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center"><strong>Score</strong></TableCell>
                      {HOLES.slice(0, 9).map(hole => (
                        <TableCell key={hole} align="center">
                          <TextField
                            type="text"
                            size="small"
                            value={scores[hole] || ''}
                            onChange={(e) => handleScoreChange(hole, e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e, hole)}
                            onFocus={(e) => {
                              setActiveHole(hole);
                              e.target.select();
                            }}
                            inputProps={{ 
                              maxLength: 2,
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                              'data-hole': hole,
                              style: { 
                                textAlign: 'center', 
                                padding: '4px', 
                                width: '40px',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                              }
                            }}
                            sx={{
                              '& input': {
                                textAlign: 'center'
                              }
                            }}
                          />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Typography variant="h6">
                          {HOLES.slice(0, 9).reduce((sum, hole) => sum + (parseInt(scores[hole]) || 0), 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center"><strong>Hole</strong></TableCell>
                      {HOLES.slice(9, 18).map(hole => (
                        <TableCell key={hole} align="center"><strong>{hole}</strong></TableCell>
                      ))}
                      <TableCell align="center"><strong>In</strong></TableCell>
                      <TableCell align="center"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center"><strong>Score</strong></TableCell>
                      {HOLES.slice(9, 18).map(hole => (
                        <TableCell key={hole} align="center">
                          <TextField
                            type="text"
                            size="small"
                            value={scores[hole] || ''}
                            onChange={(e) => handleScoreChange(hole, e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e, hole)}
                            onFocus={(e) => {
                              setActiveHole(hole);
                              e.target.select();
                            }}
                            inputProps={{ 
                              maxLength: 2,
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                              'data-hole': hole,
                              style: { 
                                textAlign: 'center', 
                                padding: '4px', 
                                width: '40px',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                              }
                            }}
                            sx={{
                              '& input': {
                                textAlign: 'center'
                              }
                            }}
                          />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Typography variant="h6">
                          {HOLES.slice(9, 18).reduce((sum, hole) => sum + (parseInt(scores[hole]) || 0), 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h5" color="primary">
                          {calculateTotal()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Quick Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit Hole {editingHole} Score</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Score"
            type="text"
            fullWidth
            value={editingScore}
            onChange={(e) => setEditingScore(e.target.value)}
            inputProps={{ 
              min: 1, 
              max: 20,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 2
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveQuickEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Scores Confirmation Dialog */}
      <Dialog open={clearAllDialogOpen} onClose={() => setClearAllDialogOpen(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ClearAll color="error" />
            Clear All Scores
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              ⚠️ WARNING: This action cannot be undone!
            </Typography>
            <Typography variant="body2">
              This will permanently delete ALL scores for ALL players in this tournament. 
              This includes:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>All hole scores for every player</li>
              <li>All scoring timestamps and history</li>
              <li>All scorer information</li>
              <li>Tournament progress data</li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              Are you absolutely sure you want to proceed?
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleClearAllScores} 
            variant="contained" 
            color="error"
            disabled={saving}
            startIcon={<ClearAll />}
          >
            {saving ? 'Clearing...' : 'Yes, Clear All Scores'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

